import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, rm, fmtDateTime, formatErr } from "@/lib/api";
import { usePlayer } from "@/lib/asv";
import { Empty } from "@/components/asv/Cards";
import { InvoiceButton } from "@/components/InvoiceButton";

const ST = { paid: "asv-accent", partially_refunded: "text-amber-500", refunded: "text-red-500" };

export function Payments() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/asv/me/payments").then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e)); setD([]); }); }, []);
  if (!d) return <div className="asv-card h-40 animate-pulse" />;
  if (!d.length) return <Empty text="No payments yet. Your paid bookings and tickets will show here with their invoices." />;
  return (
    <div className="asv-card overflow-hidden" data-testid="payments-list">
      {d.map((x) => (
        <div key={x.id} className="asv-line flex flex-wrap items-center justify-between gap-3 border-t p-4 first:border-t-0" data-testid={`payment-${x.id}`}>
          <div className="min-w-0">
            <p className="truncate font-bold">{x.vendor_name}{x.what ? ` · ${x.what}` : ""}</p>
            <p className="asv-muted text-xs">{x.type === "tickets" ? "Event tickets" : "Venue booking"} · Order {x.order_no} · {fmtDateTime(x.paid_at || x.created_at)}</p>
            <p className="asv-muted text-xs uppercase">{x.method || "-"}{x.invoice_no ? <span className="normal-case"> · {x.invoice_no}</span> : null}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right"><p className="font-extrabold" data-testid={`payment-amount-${x.id}`}>{rm(x.amount_sen)}</p>
              {!!x.refunded_sen && <p className="text-xs text-red-500">refunded {rm(x.refunded_sen)}</p>}
              <p className={`text-xs font-bold capitalize ${ST[x.status] || ""}`}>{x.status.replace("_", " ")}</p></div>
            <InvoiceButton path={`/asv/me/payments/${x.id}/invoice`} testid={`payment-invoice-${x.id}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

const Pw = ({ label, value, onChange, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <label className="block space-y-1.5"><span className="text-sm font-semibold">{label}</span>
      <span className="relative block"><input type={show ? "text" : "password"} className="asv-input pr-11" value={value} onChange={(e) => onChange(e.target.value)} autoComplete="new-password" data-testid={testid} />
        <button type="button" onClick={() => setShow(!show)} className="asv-muted absolute right-3 top-1/2 -translate-y-1/2" aria-label={show ? "Hide password" : "Show password"} data-testid={`${testid}-toggle`}>
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></span></label>
  );
};

function ChangePassword() {
  const [f, setF] = useState({ current: "", new: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.current) return toast.error("Enter your current password");
    if (f.new.length < 8) return toast.error("New password must be at least 8 characters");
    if (f.new !== f.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try { await api.post("/asv/me/password", { current: f.current, new: f.new }); toast.success("Password updated"); setF({ current: "", new: "", confirm: "" }); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="asv-card space-y-4 p-6" data-testid="change-password">
      <h3 className="asv-display text-lg font-extrabold uppercase">Change password</h3>
      <Pw label="Current password" value={f.current} onChange={(v) => setF({ ...f, current: v })} testid="pw-current" />
      <Pw label="New password (min 8 characters)" value={f.new} onChange={(v) => setF({ ...f, new: v })} testid="pw-new" />
      <Pw label="Confirm new password" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} testid="pw-confirm" />
      <button className="asv-btn" disabled={busy} onClick={save} data-testid="pw-save">{busy ? "Updating…" : "Update password"}</button>
    </div>
  );
}

export function Profile() {
  const { player, refresh } = usePlayer();
  const [f, setF] = useState({ name: player.name, phone: player.phone || "" });
  const save = () => {
    if (f.name.trim().length < 2) return toast.error("Name must be at least 2 characters");
    api.put("/asv/me", f).then(() => { toast.success("Profile saved"); refresh(); }).catch((e) => toast.error(formatErr(e)));
  };
  const field = (label, el) => <label className="block space-y-1.5"><span className="text-sm font-semibold">{label}</span>{el}</label>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="asv-card space-y-4 self-start p-6" data-testid="profile-card">
        {field("Email", <input className="asv-input opacity-70" value={player.email} disabled data-testid="profile-email" />)}
        {field("Name", <input className="asv-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name" />)}
        {field("Phone", <input className="asv-input" type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="profile-phone" />)}
        <button className="asv-btn" onClick={save} data-testid="profile-save">Save changes</button>
      </div>
      <ChangePassword />
    </div>
  );
}

export function LogoutButton() {
  const { logout } = usePlayer();
  const nav = useNavigate();
  return <button className="asv-btn-ghost" onClick={() => logout().then(() => nav("/"))} data-testid="account-logout">Log out</button>;
}
